import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cx } from "utils/join-classnames";
import { paths } from "ui/routes";
import planKeys from "core/constants/planKeys";
import { Icon, Logo, Button, Tooltip, ProjectLogo } from "ui/components";
import { PlanKey, IconNames } from "core/models";
import { useSessionContext } from "ui/context/SessionContext";
import usePermissions from "ui/hooks/usePermissions";
import styles from "./Sidebar.styles.module.scss";

interface SidebarItemProps {
    path: string;
    name: string;
    iconName: IconNames;
    tooltip?: string | null;
    disabled?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
    path,
    name,
    iconName,
    tooltip,
    disabled,
}: SidebarItemProps) => {
    const location = useLocation();
    const isActive = location.pathname === path;
    return (
        <NavLink
            className={cx(
                styles.item,
                disabled ? styles.item__disabled : null,
                isActive ? styles.is__active : null,
            )}
            to={path}
        >
            {tooltip ? (
                <Tooltip text={tooltip} className={styles.item__icon}>
                    <Icon name={iconName} />
                </Tooltip>
            ) : (
                <Icon className={styles.item__icon} name={iconName} />
            )}
            <div className={styles.item__text}>{name}</div>
        </NavLink>
    );
};
export { SidebarItem };

interface Props {
    className: string;
}

const Sidebar: React.FC<Props> = ({ className }: Props) => {
    const userPermissions = usePermissions();
    const [sidebarOpened, setSidebarOpened] = React.useState<boolean>(false);
    const [currentPlan, setCurrentPlan] = React.useState<PlanKey>();
    const sessionContext = useSessionContext();
    const location = useLocation();

    React.useEffect(() => {
        if (!sessionContext.selectedProject) {
            return;
        }
        setCurrentPlan(sessionContext.selectedProject.package);
    }, [sessionContext.selectedProject]);

    const handleProjectsClick = (e: React.MouseEvent): void => {
        if (!sessionContext.userHasMultipleProjects) {
            e.preventDefault();
        }
    };

    const handleToggleSidebar = (): void => setSidebarOpened(!sidebarOpened);

    return (
        <div className={styles.container}>
            <div
                className={cx(
                    styles.sidebar,
                    sidebarOpened ? styles["sidebar--opened"] : null,
                    className,
                )}
            >
                <div className={styles.sidebar__inner}>
                    <NavLink
                        className={cx(styles.sidebar__logo)}
                        to={paths.latestFragments}
                    >
                        <Logo type={sidebarOpened ? "horizontal" : "fignet"} />
                    </NavLink>
                    <nav className={styles.sidebar__nav}>
                        <NavLink
                            onClick={handleProjectsClick}
                            to={paths.projectsPage}
                            className={cx(
                                styles.sidebar__projects,
                                styles.item,
                                !sessionContext.userHasMultipleProjects
                                    ? styles.item__projectDisabled
                                    : null,
                                location.pathname === paths.projectsPage
                                    ? styles.is__active
                                    : null,
                            )}
                        >
                            <div className={styles.sidebar__projects__avatar}>
                                <ProjectLogo
                                    className={styles.sidebar__projects__logo}
                                    projectId={
                                        sessionContext.selectedProject?.id || 0
                                    }
                                />
                                {sessionContext.userHasMultipleProjects && (
                                    <div
                                        className={
                                            styles.sidebar__projects__badge
                                        }
                                    >
                                        <Icon name="switch" />
                                    </div>
                                )}
                            </div>
                            <div className={styles.item__text}>
                                {sessionContext.selectedProject?.name}
                            </div>
                            {sessionContext.userHasMultipleProjects && (
                                <Icon
                                    name="switch"
                                    className={styles.sidebar__projects__icon}
                                />
                            )}
                        </NavLink>
                        <div
                            className={cx(
                                styles.sidebar__hr,
                                location.pathname === "/projects" ||
                                    location.pathname === "/clips"
                                    ? styles.sidebar__hr__full
                                    : null,
                            )}
                        />
                        <SidebarItem
                            path={paths.latestFragments}
                            name="Clips"
                            iconName="clips"
                            tooltip={!sidebarOpened ? "Clips" : null}
                        />
                        <SidebarItem
                            path={paths.channels}
                            name="Livestreams"
                            iconName="livestream"
                            tooltip={!sidebarOpened ? "Livestreams" : null}
                            disabled={!userPermissions.canCreateFragment}
                        />
                        <SidebarItem
                            path="settings/profile"
                            name="Settings"
                            iconName="settings"
                            tooltip={!sidebarOpened ? "Settings" : null}
                        />
                        <SidebarItem
                            path={paths.support}
                            name="Support"
                            iconName="help"
                            tooltip={!sidebarOpened ? "Support" : null}
                        />
                    </nav>
                    <div className={styles.sidebar__bottom}>
                        <div>
                            <Button
                                type="icon"
                                theme="light"
                                icon={
                                    sidebarOpened
                                        ? "arrow-left-double"
                                        : "arrow-right-double"
                                }
                                onClick={handleToggleSidebar}
                                className={styles.sidebar__bottom__btn}
                            />
                        </div>
                        {/* Feature is hidden for now */}
                        {/* <NavLink
                            to={paths.settingsPlan}
                            className={styles.sidebar__bottom__plan}
                        >
                            Plan:
                            <br />
                            {currentPlan && planKeys[currentPlan]}
                        </NavLink> */}
                        <div className={styles.sidebar__bottom__version}>
                            {
                                SPOTON_APPVERSION
                                // Global const defined in vite.config.ts, source: package.json
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(Sidebar);
